import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from './supabase';
import { getMetroSlugs } from './metros';
import type { City } from './types';

interface CityContextValue {
  cities: City[];
  currentCity: City | null;
  metroCityIds: string[];
  loading: boolean;
  setCity: (slug: string) => void;
}

const CityContext = createContext<CityContextValue>({
  cities: [],
  currentCity: null,
  metroCityIds: [],
  loading: true,
  setCity: () => {},
});

const STORAGE_KEY = 'parkingrank_city';

function findNearest(lat: number, lon: number, list: City[]): City | null {
  let best: City | null = null;
  let bestDist = Infinity;
  for (const c of list) {
    if (!c.latitude || !c.longitude) continue;
    const d = Math.pow(c.latitude - lat, 2) + Math.pow(c.longitude - lon, 2);
    if (d < bestDist) { bestDist = d; best = c; }
  }
  return best;
}

export function CityProvider({ children }: { children: React.ReactNode }) {
  const [cities, setCities] = useState<City[]>([]);
  const [currentCity, setCurrentCity] = useState<City | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('cities')
        .select('*')
        .order('name');

      const list = (data as City[] | null) ?? [];
      setCities(list);

      // 1. Check localStorage
      const saved = localStorage.getItem(STORAGE_KEY);
      const match = list.find((c) => c.slug === saved);
      if (match) {
        setCurrentCity(match);
        setLoading(false);
        return;
      }

      // 2. Silent IP-based geolocation (cached, with fallback)
      const GEO_CACHE_KEY = 'parkingrank_geo';
      try {
        const cached = sessionStorage.getItem(GEO_CACHE_KEY);
        let geo = cached ? JSON.parse(cached) : null;
        
        if (!geo) {
          const resp = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
          if (resp.ok) {
            geo = await resp.json();
            sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(geo));
          }
        }
        
        if (geo?.latitude && geo?.longitude) {
          const nearest = findNearest(geo.latitude, geo.longitude, list);
          if (nearest) {
            setCurrentCity(nearest);
            localStorage.setItem(STORAGE_KEY, nearest.slug);
          }
        }
      } catch {
        // IP lookup failed or rate limited — no city, that's fine
      }
      setLoading(false);
    }
    load();
  }, []);

  const setCity = useCallback(
    (slug: string) => {
      const match = cities.find((c) => c.slug === slug);
      if (match) {
        setCurrentCity(match);
        localStorage.setItem(STORAGE_KEY, slug);
      }
    },
    [cities],
  );

  const metroCityIds = useMemo(() => {
    if (!currentCity) return [];
    const metroSlugs = getMetroSlugs(currentCity.slug);
    return cities.filter(c => metroSlugs.includes(c.slug)).map(c => c.id);
  }, [currentCity, cities]);

  return (
    <CityContext.Provider value={{ cities, currentCity, metroCityIds, loading, setCity }}>
      {children}
    </CityContext.Provider>
  );
}

export function useCityContext() {
  return useContext(CityContext);
}
